import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import ReactPlayer from 'react-player';
import classNames from 'classnames';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import { BASE_URL, IMAGE_PATHS, VIDEO_PATHS } from '../../../../constants/paths.constants';
import { yellow, purple, darkGrey, borderGrey, universalStyles } from '../../../../app/theme';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  // Boxes & Containers
  checkBox: {
    background:
      'transparent linear-gradient(270deg, #F7B92B00 0%, #F7B92B19 34%, #F7B92B4D 58%, #F7B92B80 87%, #F7B92B98 100%) 0% 0% no-repeat padding-box',
    display: 'flex',
    marginBottom: 15,
    '&:first-of-type': {
      marginTop: 45,
    },
  },
  flexContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  diagramContainer: {
    width: '40%',
  },
  bottomTextArea: {
    width: '50%',
    paddingRight: 25,
  },
  bottomImageArea: {
    paddingLeft: 85,
    width: '50%',
    '& > p': {
      marginLeft: 10,
      marginRight: 10,
    },
  },
  videoContainer: {
    border: `6px solid ${yellow}`,
  },
  // Media
  image: {
    width: '100%',
  },
  checkImage: {
    width: 39,
    height: 39,
    marginLeft: 30,
    marginTop: 20,
    marginBottom: 20,
  },
  lymphVideo: {
    maxHeight: 480,
    aspectRatio: '.47',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: 6,
    display: 'block',
    border: `.75px solid ${borderGrey}`,
  },
  // Headers
  header: {
    marginTop: 53,
  },
  imageHeader: {
    fontSize: 22,
    fontWeight: 600,
    marginBottom: 25,
    textAlign: 'center',
  },
  videoHeader: {
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: 5,
    marginTop: 5,
  },
  checkText: {
    color: darkGrey,
    fontSize: 20,
    fontWeight: 600,
    marginTop: 12,
    marginBottom: 10,
    marginLeft: 20,
    width: '85%',
    verticalAlign: 'middle',
  },
  checkTextHeader: {
    color: darkGrey,
    fontSize: 20,
    lineHeight: 1.3,
    fontWeight: 600,
  },
  diagramImage: {
    width: '100%',
  },
  paragraph: {
    marginBottom: 15,
  },
  super: {
    fontSize: 11,
  },
  // Lists
  checklist: {
    marginLeft: '50px',
    fontSize: 18,
    lineHeight: 1.3,
    color: darkGrey,
    '& > li::before': {
      content: '"\u2022"',
      color: purple,
    },
  },
  orderedList: {
    marginLeft: '1em',
    counterReset: 'li',
    '& > li': {
      counterIncrement: 'li',
      padding: '4px 0',
    },
    '& > li::before': {
      content: 'counter(li) ". "',
      marginLeft: '-1em',
    },
    '& > ul > li': {
      padding: '4px 0 4px 20px',
    },
  },
}));

type InterpretationPage09Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const InterpretationPage09 = ({ setNextDisabled }: InterpretationPage09Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        {/* Header */}
        <img
          className={classes.image}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_1.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_1.alt}`}
        />
        {/* Main Content */}
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 1:</Typography>
          <Typography variant="h3">Review General Interpretation Criteria and Read Methodology</Typography>
          {/* Checkbox */}
          <div className={classes.checkBox}>
            <img
              className={classes.checkImage}
              src={`${BASE_URL}${IMAGE_PATHS.CHECKMARK.path}`}
              alt={`${IMAGE_PATHS.CHECKMARK.alt}`}
            />
            <Typography className={classes.checkText}>
              Localization of prostate cancer recurrence in typical sites is based on Axumin uptake in comparison with
              tissue background
            </Typography>
          </div>
          {/* Checkbox */}
          <div className={classes.checkBox}>
            <img
              className={classes.checkImage}
              src={`${BASE_URL}${IMAGE_PATHS.CHECKMARK.path}`}
              alt={`${IMAGE_PATHS.CHECKMARK.alt}`}
            />
            <Typography className={classes.checkText}>
              Imaging interpretation is predominantly qualitative, based on typical sites of recurrence of prostate
              cancer
            </Typography>
          </div>
          {/* Checkbox */}
          <div className={classes.checkBox}>
            <img
              className={classes.checkImage}
              src={`${BASE_URL}${IMAGE_PATHS.CHECKMARK.path}`}
              alt={`${IMAGE_PATHS.CHECKMARK.alt}`}
            />
            <div className={classes.checkText}>
              <Typography className={classes.checkTextHeader}>
                General guidance is to report “inside-out” (ie, assess central areas of focal uptake before assessing
                periphery)
              </Typography>
              <ul className={classNames(classes.list, classes.checklist)}>
                <li>
                  It is critically important for the reader to know typical sites of metastases and recurrence of
                  cancers, as well as the normal distribution of Axumin, normal variants, and potential pitfalls
                </li>
                <li>
                  <span>
                    When interpreting Axumin scans, the physician should be aware of differences in biodistribution
                    between other commonly used radiopharmaceuticals, particularly&nbsp;
                    <sup className={classes.super}>18</sup>
                    F-FDG, and Axumin
                  </span>
                </li>
              </ul>
            </div>
          </div>
          {/* Checkbox */}
          <div className={classes.checkBox}>
            <img
              className={classes.checkImage}
              src={`${BASE_URL}${IMAGE_PATHS.CHECKMARK.path}`}
              alt={`${IMAGE_PATHS.CHECKMARK.alt}`}
            />
            <Typography className={classes.checkText}>
              As with any imaging, full correlation with all available information (eg, medical history, laboratory
              results, bone scans, CT and MRI scans) should occur and may improve the confidence level of interpretation
              of the PET scan
            </Typography>
          </div>
          {/* Evaluation */}
          <Typography className={classes.header} variant="h5">
            Before Evaluating Scans, Consider These Steps
          </Typography>
          <Typography className={classes.paragraph} variant="body1">
            When reviewing cases with Axumin PET/CT images, it may be beneficial to follow this workflow
          </Typography>
          <Typography className={classes.paragraph} variant="body1">
            When available:
          </Typography>
          <ol className={classNames(classes.list, classes.orderedList)}>
            <li>Evaluate the maximum intensity projection (MIP) image</li>
            <li>Review the PET images in all plane views</li>
            <li>Concentrate on CT (keeping an eye on PET)</li>
            <ul className={classes.list}>
              <li>Brain windows</li>
              <li>Bone windows (cranial to caudal)</li>
              <li>Soft tissue abdomen and liver windows</li>
              <li>Soft tissue and lung windows (chest)</li>
            </ul>
            <li>Slice by slice cranial to caudal</li>
            <li>Synthesize data: Axumin PET, other imaging, clinical history, and pathology</li>
          </ol>
          {/* Diagrams */}
          <Typography className={classes.header} variant="h5">
            Tissue Background Is Measured in Blood Pool or Bone Marrow
          </Typography>
          <div className={classes.flexContainer}>
            <div className={classes.diagramContainer}>
              <Typography variant="body1" className={classes.imageHeader}>
                Volume of interest for blood pool
              </Typography>
              <img
                className={classes.diagramImage}
                src={`${BASE_URL}${IMAGE_PATHS.BLOOD_POOL_DIAGRAM.path}`}
                alt={`${IMAGE_PATHS.BLOOD_POOL_DIAGRAM.alt}`}
              />
            </div>
            <div className={classes.diagramContainer}>
              <Typography variant="body1" className={classes.imageHeader}>
                Volume of interest for bone marrow
              </Typography>
              <img
                className={classes.diagramImage}
                src={`${BASE_URL}${IMAGE_PATHS.BONE_MARROW_DIAGRAM.path}`}
                alt={`${IMAGE_PATHS.BONE_MARROW_DIAGRAM.alt}`}
              />
            </div>
          </div>
          {/* Interpretation */}
          <Typography className={classes.header} variant="h5">
            General Interpretation Criteria
          </Typography>
          <div className={classes.flexContainer}>
            <div className={classes.bottomTextArea}>
              <ul className={classNames(classes.list)}>
                <li>
                  <span>
                    Thresholds based on lymph node sizes (ie, &#60; or &#8805; 1 cm), referred to throughout the
                    interpretation criteria, are based on the
                    <b> maximum </b>
                    dimension of the lymph node for the purpose of visual interpretation
                  </span>
                </li>
                <ul className={classNames(classes.list, classes.nestedList)}>
                  <li>
                    Nodal short axis or bi-dimensional measurements should still be reported per the interpreting
                    reader’s usual practice
                  </li>
                </ul>
                <li>SUV measurements should be normalized by body weight</li>
                <li>Use of multiple orthogonal planes of view (eg, axial, sagittal, and coronal) is recommended</li>
                <li>
                  Use of color tables are a personal preference for the reader, based on the image review workstation
                  and reader’s experience
                </li>
              </ul>
            </div>
            <div className={classes.bottomImageArea}>
              <Typography variant="body1">
                Maximum intensity projection may be useful for the detection of lesions in bone, as well as in lymph
                nodes
              </Typography>
              <div className={classes.videoContainer}>
                <Typography variant="body1" className={classes.videoHeader}>
                  Lymph Node
                </Typography>
                <div className={classes.lymphVideo}>
                  <ReactPlayer className={classes.responsiveVideo} url={VIDEO_PATHS.LYMPH.path} controls />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LearningCheckQuestions module="axuminInterpretation" page={9} setNextDisabled={setNextDisabled} trainingId={1} />
    </>
  );
};

export default InterpretationPage09;
